import React, { useEffect, useMemo, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { CustomField } from '../../types'
import { EditDialog, FormField, SelectInput, TextInput } from '../common/EditDialog'
import {
  COUNTER_VARIABLE_EXCLUDED_TYPES,
  CounterConfigEditor,
  CounterConfigValue,
  defaultCounterConfig,
  normalizeCounterConfig,
  parseTemplateVariableNames,
  toCounterConfigPayload,
  validateCounterConfig,
} from './CounterConfigEditor'

interface CustomFieldEditDialogProps {
  object?: CustomField | null
  isCreating: boolean
  onSuccess: (createdObject?: CustomField) => void
  onError: (error: any) => void
  onClose: () => void
}

const generateOptionId = () => Math.random().toString(36).slice(2, 18).padEnd(16, '0').slice(0, 16)

const normalizeSelectOptions = (options: Array<{ id?: string; label?: string }> = []) => {
  const usedIds = new Set<string>()
  return options.map((option) => {
    const label = option?.label || ''
    let id = typeof option?.id === 'string' ? option.id : ''
    if (!id.trim() || usedIds.has(id)) {
      id = generateOptionId()
    }
    usedIds.add(id)
    return { id, label }
  })
}

export const CustomFieldEditDialog: React.FC<CustomFieldEditDialogProps> = ({ object, isCreating, onSuccess, onError, onClose }) => {
  const { t } = useTranslation()

  const [eligibleAttributeFields, setEligibleAttributeFields] = useState<CustomField[]>([])

  const [formData, setFormData] = useState({
    name: '',
    data_type: 'string',
    placeholder: '',
    default_value: null as any,
    extra_data: {
      select_options: [] as Array<{ id: string; label: string }>,
      default_currency: '',
      counter: defaultCounterConfig(),
    },
    counter_type_configs: [] as Array<{
      document_type_id: number
      document_type_name: string
      template: string | null
    }>,
  })

  useEffect(() => {
    const loadEligibleFields = async () => {
      try {
        const response = await customFieldService.listAll()
        setEligibleAttributeFields((response.results || []).filter((field) => !COUNTER_VARIABLE_EXCLUDED_TYPES.has(field.data_type)))
      } catch {
        setEligibleAttributeFields([])
      }
    }
    loadEligibleFields()
  }, [])

  useEffect(() => {
    if (object) {
      setFormData({
        name: object.name,
        data_type: object.data_type,
        placeholder: object.placeholder || '',
        default_value: object.default_value ?? null,
        extra_data: {
          select_options: normalizeSelectOptions(object.extra_data?.select_options),
          default_currency: object.extra_data?.default_currency || '',
          counter: normalizeCounterConfig(object.extra_data?.counter),
        },
        counter_type_configs: (object.counter_type_configs || []).map((item) => ({
          document_type_id: item.document_type_id,
          document_type_name: item.document_type_name,
          template: item.counter_config?.template ?? null,
        })),
      })
    } else {
      setFormData({
        name: '',
        data_type: 'string',
        placeholder: '',
        default_value: null,
        extra_data: {
          select_options: [],
          default_currency: '',
          counter: defaultCounterConfig(),
        },
        counter_type_configs: [],
      })
    }
  }, [object])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleExtraDataChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      extra_data: {
        ...prev.extra_data,
        [field]: value,
      },
    }))
  }

  const handleCounterConfigChange = (counter: CounterConfigValue) => {
    setFormData((prev) => ({
      ...prev,
      extra_data: {
        ...prev.extra_data,
        counter,
      },
    }))
  }

  const handleTypeTemplateChange = (documentTypeId: number, template: string | null) => {
    setFormData((prev) => ({
      ...prev,
      counter_type_configs: prev.counter_type_configs.map((item) =>
        item.document_type_id === documentTypeId ? { ...item, template } : item
      ),
    }))
  }

  const validateTypeTemplate = (template: string, tFn: (key: string) => string): string | null => {
    const trimmed = template.trim()
    if (!trimmed) {
      return tFn('counter_template_required')
    }
    const counterCount = parseTemplateVariableNames(trimmed).filter((name) => name === 'counter').length
    if (counterCount === 0) {
      return tFn('counter_template_missing_counter')
    }
    if (counterCount > 1) {
      return tFn('counter_template_duplicate_counter')
    }
    return null
  }

  const addSelectOption = () => {
    if (hasUnfilledSelectOption) {
      return
    }

    const newOption = { id: generateOptionId(), label: '' }
    setFormData((prev) => ({
      ...prev,
      extra_data: {
        ...prev.extra_data,
        select_options: [...prev.extra_data.select_options, newOption],
      },
    }))
  }

  const removeSelectOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      extra_data: {
        ...prev.extra_data,
        select_options: prev.extra_data.select_options.filter((_, i) => i !== index),
      },
    }))
  }

  const updateSelectOption = (index: number, field: 'id' | 'label', value: string) => {
    setFormData((prev) => {
      const newOptions = [...prev.extra_data.select_options]
      newOptions[index] = { ...newOptions[index], [field]: value }
      return {
        ...prev,
        extra_data: {
          ...prev.extra_data,
          select_options: newOptions,
        },
      }
    })
  }

  const getTitle = () => {
    return isCreating ? t('create_new_custom_field') : t('edit_custom_field')
  }

  const canChangeCustomField = permissionsService.currentUserCan(PermissionAction.Change, PermissionType.CustomField)

  const isCounter = formData.data_type === 'counter'
  const isSeparator = formData.data_type === 'separator'

  const payloadFormData = useMemo(() => {
    if (!isCounter) {
      const { counter_type_configs: _counterTypeConfigs, ...data } = formData
      return data
    }
    return {
      ...formData,
      placeholder: '',
      default_value: null,
      extra_data: {
        counter: toCounterConfigPayload(formData.extra_data.counter),
      },
      counter_type_configs: formData.counter_type_configs.map((item) => ({
        document_type_id: item.document_type_id,
        counter_config: item.template != null ? { template: item.template } : null,
      })),
    }
  }, [formData, isCounter])

  const validateForm = (data: typeof formData): string[] => {
    const errors: string[] = []

    if (!data.name || data.name.trim() === '') {
      errors.push('name:Name is required')
    }

    if (data.data_type === 'counter') {
      errors.push(...validateCounterConfig(data.extra_data.counter, t))
      data.counter_type_configs.forEach((item) => {
        if (item.template == null) {
          return
        }
        const message = validateTypeTemplate(item.template, t)
        if (message) {
          errors.push(`counter_type_${item.document_type_id}:${message}`)
        }
      })
    }

    return errors
  }

  const dataTypeOptions = [
    { value: 'string', label: t('text') },
    { value: 'url', label: t('url') },
    { value: 'date', label: t('date') },
    { value: 'boolean', label: t('boolean') },
    { value: 'integer', label: t('number') },
    { value: 'float', label: t('decimal') },
    { value: 'monetary', label: t('monetary') },
    { value: 'documentlink', label: t('document_link') },
    { value: 'longtext', label: t('long_text') },
    { value: 'select', label: t('selection') },
    { value: 'counter', label: t('counter_data_type') },
    { value: 'separator', label: t('separator') },
  ]

  const hasUnfilledSelectOption = formData.extra_data.select_options.some((option) => option.label.trim() === '')

  const getFormFields = (errors?: Record<string, string>) => (
    <>
      <FormField label={t('name')} required error={errors?.name}>
        <TextInput
          value={formData.name || ''}
          onChange={(value) => handleInputChange('name', value)}
          placeholder={t('enter_name')}
          required
          autoComplete="off"
          error={errors?.name}
          disabled={!isCreating && !canChangeCustomField}
        />
      </FormField>

      <FormField label={t('type')} required>
        <SelectInput
          value={formData.data_type}
          onChange={(value) => handleInputChange('data_type', value)}
          options={dataTypeOptions}
          disabled={!isCreating}
        />
        {!isCreating && <div className="form-text text-muted mt-1">{t('data_type_cannot_be_changed_after_creation')}</div>}
      </FormField>

      {isSeparator && <div className="form-text text-muted mb-3">{t('separator_attribute_help')}</div>}

      {isCounter && (
        <>
          <CounterConfigEditor
            value={formData.extra_data.counter}
            onChange={handleCounterConfigChange}
            eligibleAttributeFields={eligibleAttributeFields}
            excludeFieldId={object?.id}
            disabled={!isCreating && !canChangeCustomField}
            error={errors?.counter_template}
            helpText={`${t('counter_attribute_help')} ${t('counter_attribute_default_help')}`}
          />

          {!isCreating && (
            <FormField label={t('counter_settings_by_document_type')}>
              <div className="form-text text-muted mb-2">{t('counter_settings_by_document_type_help')}</div>
              {formData.counter_type_configs.length === 0 ? (
                <div className="text-muted small">{t('counter_no_document_types')}</div>
              ) : (
                formData.counter_type_configs.map((item) => {
                  const hasOverride = item.template != null
                  return (
                    <div key={item.document_type_id} className="border rounded p-3 mb-2">
                      <div className="fw-semibold mb-2">{item.document_type_name}</div>
                      <Form.Check
                        type="switch"
                        className="mb-2"
                        label={t('counter_use_custom_for_type')}
                        checked={hasOverride}
                        disabled={!canChangeCustomField}
                        onChange={(event) =>
                          handleTypeTemplateChange(
                            item.document_type_id,
                            event.target.checked ? formData.extra_data.counter.template || '{{year}}/{{counter}}' : null
                          )
                        }
                      />
                      {hasOverride && (
                        <>
                          <TextInput
                            value={item.template || ''}
                            onChange={(value) => handleTypeTemplateChange(item.document_type_id, value)}
                            placeholder="F-{{counter}}/{{year}}"
                            disabled={!canChangeCustomField}
                            error={errors?.[`counter_type_${item.document_type_id}`]}
                          />
                          <div className="form-text text-muted mt-1">{t('counter_type_template_help')}</div>
                        </>
                      )}
                    </div>
                  )
                })
              )}
            </FormField>
          )}
        </>
      )}

      {!isSeparator && !isCounter && (
        <FormField label={t('placeholder')}>
          <TextInput
            value={formData.placeholder || ''}
            onChange={(value) => handleInputChange('placeholder', value)}
            placeholder={t('placeholder')}
            autoComplete="off"
            disabled={!isCreating && !canChangeCustomField}
          />
        </FormField>
      )}

      {formData.data_type === 'select' && (
        <FormField label={t('select_options')}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Button
              variant="primary"
              size="sm"
              onClick={addSelectOption}
              type="button"
              disabled={(!isCreating && !canChangeCustomField) || hasUnfilledSelectOption}
            >
              <i className="bi bi-plus-circle"></i> {t('add_option')}
            </Button>
          </div>
          {formData.extra_data.select_options.map((option, index) => (
            <div key={index} className="input-group input-group-sm mb-2">
              <TextInput
                value={option.label}
                onChange={(value) => updateSelectOption(index, 'label', value)}
                placeholder={t('option_label')}
                disabled={!isCreating && !canChangeCustomField}
              />
              <Button
                variant="outline-danger"
                onClick={() => removeSelectOption(index)}
                type="button"
                disabled={!isCreating && !canChangeCustomField}
              >
                <i className="bi bi-trash"></i> {t('delete')}
              </Button>
            </div>
          ))}
          {formData.extra_data.select_options.length === 0 && <div className="form-text text-muted">{t('no_select_options_add_some')}</div>}
        </FormField>
      )}

      {formData.data_type === 'monetary' && (
        <FormField label={t('default_currency')}>
          <TextInput
            value={formData.extra_data.default_currency || ''}
            onChange={(value) => handleExtraDataChange('default_currency', value.toUpperCase())}
            placeholder={t('currency_code_placeholder')}
            type="text"
            disabled={!isCreating && !canChangeCustomField}
          />
          <div className="form-text text-muted mt-1">{t('three_character_currency_code')}</div>
        </FormField>
      )}

      {!isSeparator && !isCounter && (
        <FormField label={t('default_value')}>
          {formData.data_type === 'boolean' ? (
            <Form.Check
              type="checkbox"
              checked={!!formData.default_value}
              onChange={(e) => handleInputChange('default_value', e.target.checked)}
              disabled={!isCreating && !canChangeCustomField}
              label={t('default_value')}
            />
          ) : formData.data_type === 'date' ? (
            <Form.Control
              type="date"
              value={formData.default_value || ''}
              onChange={(e) => handleInputChange('default_value', e.target.value || null)}
              disabled={!isCreating && !canChangeCustomField}
            />
          ) : formData.data_type === 'integer' ? (
            <Form.Control
              type="number"
              step="1"
              value={formData.default_value ?? ''}
              onChange={(e) => handleInputChange('default_value', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              disabled={!isCreating && !canChangeCustomField}
            />
          ) : formData.data_type === 'float' ? (
            <Form.Control
              type="number"
              step="0.01"
              value={formData.default_value ?? ''}
              onChange={(e) => handleInputChange('default_value', e.target.value === '' ? null : parseFloat(e.target.value))}
              disabled={!isCreating && !canChangeCustomField}
            />
          ) : formData.data_type === 'select' ? (
            <Form.Select
              value={formData.default_value || ''}
              onChange={(e) => handleInputChange('default_value', e.target.value || null)}
              disabled={!isCreating && !canChangeCustomField}
            >
              <option value="">{t('select_option')}</option>
              {formData.extra_data.select_options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : formData.data_type === 'documentlink' ? (
            <Form.Control
              type="text"
              value={Array.isArray(formData.default_value) ? formData.default_value.join(',') : ''}
              onChange={(e) => {
                const parsed = e.target.value
                  .split(',')
                  .map((item) => item.trim())
                  .filter((item) => item !== '')
                  .map((item) => parseInt(item, 10))
                  .filter((num) => !Number.isNaN(num))
                handleInputChange('default_value', parsed.length > 0 ? parsed : null)
              }}
              placeholder="1,2,3"
              disabled={!isCreating && !canChangeCustomField}
            />
          ) : (
            <TextInput
              value={formData.default_value ?? ''}
              onChange={(value) => handleInputChange('default_value', value === '' ? null : value)}
              placeholder={t('default_value')}
              autoComplete="off"
              disabled={!isCreating && !canChangeCustomField}
            />
          )}
        </FormField>
      )}
    </>
  )

  return (
    <EditDialog
      object={object}
      isCreating={isCreating}
      onSuccess={onSuccess}
      onError={onError}
      onClose={onClose}
      service={customFieldService}
      formData={payloadFormData}
      getFormFields={getFormFields}
      getTitle={getTitle}
      validateForm={validateForm}
      mapServerFieldErrors={(fieldErrors) => {
        const { extra_data, ...rest } = fieldErrors
        if (!extra_data) {
          return fieldErrors
        }
        return {
          ...rest,
          counter_template: fieldErrors.counter_template || extra_data,
        }
      }}
      canEdit={canChangeCustomField}
    />
  )
}
